import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery } from '@apollo/client/react';
import { canApproveAgentCommand, type AppRole } from '@infras/shared-types';
import {
  AGENT_COMMAND_PLAN,
  APPROVE_AGENT_COMMAND,
  DRAFT_AGENT_COMMAND,
  OP_FIELDS,
  OP_LABEL,
  isMutatingOp,
  isPlanConflict,
  serverMessage,
  type AgentCommand,
  type AgentCommandPlan,
} from '../lib/agentCommands';

/**
 * The one place a command is issued: PREVIEW → DRAFT → APPROVE.
 *
 * Preview (`agentCommandPlan`) is for the human's eyes and persists nothing.
 * Drafting freezes the plan — including server-generated values such as
 * `restartedAt` — and returns the digest that approval must echo back. So the
 * approve call sends THE DRAFT'S OWN `planDigest`, never a digest from a later
 * preview: re-previewing the same inputs legitimately produces a different
 * digest and the server would (correctly) refuse it.
 *
 * A mutating op on a production cluster additionally needs the cluster name
 * typed out, and is root-only — a maintainer can draft it and nothing more.
 */
export interface AgentCommandDialogProps {
  clusterName: string;
  op: string;
  agentId?: string | null;
  /** Prefill for the arg form (e.g. the DaemonSet name behind a Restart button). */
  initialArgs?: Record<string, string>;
  role: AppRole;
  onClose: () => void;
}

export function AgentCommandDialog({
  clusterName,
  op: initialOp,
  agentId,
  initialArgs,
  role,
  onClose,
}: AgentCommandDialogProps) {
  const [op, setOp] = useState(initialOp);
  const [values, setValues] = useState<Record<string, string>>(initialArgs ?? {});
  const [typed, setTyped] = useState('');
  const [note, setNote] = useState('');
  const [draft, setDraft] = useState<AgentCommand | null>(null);
  const [approved, setApproved] = useState<AgentCommand | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const fields = OP_FIELDS[op] ?? [];
  const missing = fields.filter((f) => f.required && !values[f.key]?.trim()).map((f) => f.label);
  const argsJson = useMemo(() => JSON.stringify(buildArgs(op, values)), [op, values]);

  // Debounce so typing a pod name isn't a plan query per keystroke.
  const [previewArgs, setPreviewArgs] = useState(argsJson);
  useEffect(() => {
    const t = setTimeout(() => setPreviewArgs(argsJson), 350);
    return () => clearTimeout(t);
  }, [argsJson]);

  const planQ = useQuery<{ agentCommandPlan: AgentCommandPlan }>(AGENT_COMMAND_PLAN, {
    variables: { clusterName, op, args: previewArgs, agentId: agentId ?? null },
    fetchPolicy: 'network-only',
    skip: missing.length > 0 || !!draft,
  });
  const plan = planQ.data?.agentCommandPlan;

  const [draftCmd] = useMutation<{ draftAgentCommand: AgentCommand }>(DRAFT_AGENT_COMMAND);
  const [approveCmd] = useMutation<{ approveAgentCommand: AgentCommand }>(APPROVE_AGENT_COMMAND);

  // Blast-radius facts. The plan query stops once a draft exists, so latch the
  // last previewed values — otherwise `production` would snap back to its safe
  // default and mis-gate approval on a non-prod cluster.
  const [production, setProduction] = useState(true);
  const [warnings, setWarnings] = useState<string[]>([]);
  useEffect(() => {
    if (!plan) return;
    setProduction(plan.production);
    setWarnings(plan.warnings);
  }, [plan]);

  const mutating = draft?.mutating ?? plan?.mutating ?? isMutatingOp(op);
  const canApprove = canApproveAgentCommand(role, { mutating, production });
  const needsTyped = production && mutating;
  const typedOk = !needsTyped || typed.trim() === clusterName;

  async function onDraft() {
    setErr(null);
    setBusy(true);
    try {
      const res = await draftCmd({
        variables: { clusterName, op, args: argsJson, agentId: agentId ?? null },
      });
      if (res.data?.draftAgentCommand) setDraft(res.data.draftAgentCommand);
    } catch (e) {
      setErr(serverMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function onApprove() {
    if (!draft?.planDigest) return;
    setErr(null);
    setBusy(true);
    try {
      // THE DRAFT'S OWN digest — not a freshly previewed one.
      const res = await approveCmd({
        variables: { id: draft.id, planDigest: draft.planDigest, note: note.trim() || null },
      });
      if (res.data?.approveAgentCommand) setApproved(res.data.approveAgentCommand);
    } catch (e) {
      const msg = serverMessage(e);
      setErr(msg);
      // The plan moved under us — drop back to preview. Clearing the draft
      // un-skips the plan query, so the operator reads the NEW diff before
      // drafting again (approving the stale digest would only be refused).
      if (isPlanConflict(msg)) setDraft(null);
    } finally {
      setBusy(false);
    }
  }

  const frozenDiff = draft?.planDiff ?? plan?.diff ?? '';
  const summary = draft?.planSummary ?? plan?.summary ?? '';

  return (
    <div
      className="confirm-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="confirm-card cmd-card" role="dialog" aria-modal="true" aria-label="Issue agent command">
        <div className="cmd-head">
          <h3 className="confirm-title">{OP_LABEL[op] ?? op}</h3>
          <span className="badge quiet">{op}</span>
          {mutating ? <span className="badge inferred">mutating</span> : <span className="badge">read-only</span>}
          {production && <span className="badge prod">production</span>}
          <span className="spacer" />
          <button type="button" className="mini-btn" onClick={onClose}>
            close
          </button>
        </div>

        {/* Cluster + agent id are resource names, so Plex Sans — the mono policy
            ratified in phase 6 §1. */}
        <div className="cmd-target muted">
          Target: {clusterName}
          {' · '}
          {agentId ? (
            <>agent {agentId}</>
          ) : (
            <>cluster leader{plan?.target ? ` (${plan.target})` : ''}</>
          )}
        </div>

        {approved ? (
          <div className="cmd-done">
            <p>
              <span className={`badge confirmed`}>{approved.status}</span> Approved — the agent will pick it up
              on its next poll.
            </p>
            <p>
              <Link to={`/agents/commands/${approved.id}`} onClick={onClose}>
                View command #{approved.id} →
              </Link>
            </p>
            <div className="btn-row">
              <button type="button" className="btn btn-ghost" onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            {!draft && (
              <>
                <div className="cmd-fields">
                  <label className="cmd-field">
                    <span className="cmd-field-k">Operation</span>
                    <select
                      className="class-select"
                      value={op}
                      onChange={(e) => {
                        setOp(e.target.value);
                        setValues({});
                        setErr(null);
                      }}
                    >
                      {Object.keys(OP_FIELDS).map((o) => (
                        <option key={o} value={o}>
                          {OP_LABEL[o] ?? o}
                        </option>
                      ))}
                    </select>
                  </label>
                  {fields.map((f) => (
                    <label className="cmd-field" key={f.key}>
                      <span className="cmd-field-k">
                        {f.label}
                        {f.required && <span className="cmd-req"> *</span>}
                        {f.help && <span className="muted"> — {f.help}</span>}
                      </span>
                      {f.kind === 'select' ? (
                        <select
                          className="class-select"
                          value={values[f.key] ?? ''}
                          onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                        >
                          <option value="">(choose)</option>
                          {(f.options ?? []).map((o) => (
                            <option key={o} value={o}>
                              {o}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          className="class-select"
                          type={f.kind === 'number' ? 'number' : 'text'}
                          placeholder={f.placeholder}
                          value={values[f.key] ?? ''}
                          onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                        />
                      )}
                    </label>
                  ))}
                </div>

                {missing.length > 0 && (
                  <p className="muted cmd-hint">Fill {missing.join(', ')} to render the plan.</p>
                )}
              </>
            )}

            {planQ.error && !draft && <p className="run-error">{serverMessage(planQ.error)}</p>}

            {(plan || draft) && (
              <>
                <div className="cmd-summary">{summary}</div>
                {warnings.length > 0 && (
                  <ul className="cmd-warnings">
                    {warnings.map((w) => (
                      <li key={w}>{w}</li>
                    ))}
                  </ul>
                )}
                <div className="log-viewer cmd-diff">
                  <pre>{frozenDiff || '(no diff)'}</pre>
                </div>
              </>
            )}

            {draft && (
              <p className="muted cmd-hint">
                Draft #{draft.id} created — the plan above is <strong>frozen</strong>. Approving
                dispatches exactly this.
              </p>
            )}

            {err && (
              <p className="run-error">
                {err}
                {isPlanConflict(err) && (
                  <>
                    <br />
                    Review the refreshed diff above, then draft and approve again.
                  </>
                )}
              </p>
            )}

            {draft && canApprove && needsTyped && (
              <label className="cmd-field cmd-confirm">
                <span className="cmd-field-k">
                  Type <strong>{clusterName}</strong> to confirm this production change
                </span>
                <input
                  className="class-select"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder={clusterName}
                  autoComplete="off"
                />
              </label>
            )}

            {draft && canApprove && (
              <label className="cmd-field">
                <span className="cmd-field-k">Note (optional)</span>
                <input className="class-select" value={note} onChange={(e) => setNote(e.target.value)} />
              </label>
            )}

            {draft && !canApprove && (
              <p className="muted cmd-hint">
                A mutating operation on a production cluster needs <strong>root</strong> approval. The draft is
                filed — send{' '}
                <Link to={`/agents/commands/${draft.id}`} onClick={onClose}>
                  #{draft.id}
                </Link>{' '}
                to a root.
              </p>
            )}

            <div className="btn-row">
              {!draft ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={busy || missing.length > 0 || !plan}
                  onClick={onDraft}
                >
                  {busy ? 'Working…' : canApprove ? 'Create draft' : 'Request root approval'}
                </button>
              ) : canApprove ? (
                <button
                  type="button"
                  className={mutating ? 'btn btn-danger' : 'btn btn-primary'}
                  disabled={busy || !typedOk || !draft.planDigest}
                  onClick={onApprove}
                >
                  {busy ? 'Approving…' : mutating ? 'Approve & dispatch' : 'Approve'}
                </button>
              ) : null}
              <button type="button" className="btn btn-ghost" disabled={busy} onClick={onClose}>
                {draft ? 'Close' : 'Cancel'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Drop blanks, coerce numeric fields — the server re-validates either way. */
function buildArgs(op: string, values: Record<string, string>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of OP_FIELDS[op] ?? []) {
    const raw = values[f.key]?.trim();
    if (!raw) continue;
    out[f.key] = f.kind === 'number' ? Number(raw) : raw;
  }
  return out;
}
